import { useState } from 'react'
import { formatCurrency } from '../lib/utils'

// Delivery-refund reason labels — keep in sync with the worker's
// `REFUND_REASON_CODES` set (`backend/worker/src/routes/admin/orders.ts`,
// the POST /:id/refund route). Change here and there together.
export const REFUND_REASON_LABELS: Record<string, string> = {
  damaged_in_delivery: 'Damaged in delivery',
  wrong_item: 'Wrong item',
  not_received: 'Not received',
  quality_issue: 'Quality issue',
  customer_cancelled: 'Customer cancelled',
  store_cancelled: 'Store cancelled',
  duplicate_charge: 'Duplicate charge',
  fraud: 'Fraud / unauthorised',
  other: 'Other',
}

interface Props {
  orderNumber: string
  amountFils: number
  // Modal copy branches on whether the refund will land as a void (auth
  // still held) or a real refund (card was captured). Just controls the
  // header text + primary-button label — the reason set is identical.
  action: 'void' | 'refund'
  isSubmitting: boolean
  errorMessage: string | null
  onCancel: () => void
  onConfirm: (code: string, text?: string) => void
}

/// Modal for issuing a delivery-order refund. Chip set + free-text
/// 'Other' handling mirror the POS void modal. Calls `POST
/// /admin/orders/:id/refund` via the parent's onConfirm handler.
/// The worker route validates the reason against REFUND_REASON_CODES,
/// persists it on `orders.refund_reason_code` + `orders.refund_reason`,
/// and includes both in the audit-log after-JSON so finance can
/// categorise refunds after the fact.
export function RefundReasonModal({
  orderNumber, amountFils, action, isSubmitting, errorMessage, onCancel, onConfirm,
}: Props) {
  const [code, setCode] = useState<string | null>(null)
  const [text, setText] = useState('')
  const submitDisabled =
    isSubmitting ||
    code === null ||
    (code === 'other' && text.trim().length < 3)

  const title = action === 'void'
    ? `Void authorization ${orderNumber}`
    : `Refund ${orderNumber}`
  const blurb = action === 'void'
    ? `Releases the ${formatCurrency(amountFils)} hold on the customer's card — they'll never see a charge. Wallet tender (gift card / store credit) is restored. Audit-logged with the reason.`
    : `Refunds ${formatCurrency(amountFils)} back to the customer's card. Wallet tender (gift card / store credit) is restored on undelivered orders. Audit-logged with the reason.`
  const primaryLabel = action === 'void' ? 'Void authorization' : 'Issue refund'
  const submittingLabel = action === 'void' ? 'Voiding…' : 'Refunding…'

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70" onClick={onCancel} />
      <div className="relative w-full max-w-md rounded-lg border border-[#2e2e2e] bg-[#1a1a1a] shadow-2xl">
        <div className="px-5 py-4 border-b border-[#2e2e2e]">
          <p className="text-white font-semibold">{title}</p>
          <p className="text-gray-500 text-xs mt-1">{blurb}</p>
        </div>
        <div className="px-5 py-4 space-y-3">
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Reason</p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(REFUND_REASON_LABELS).map(([k, label]) => {
                const selected = code === k
                return (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setCode(k)}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                      selected
                        ? 'bg-red-500/20 border-red-500/60 text-red-200'
                        : 'bg-white/5 border-white/15 text-gray-300 hover:bg-white/10'
                    }`}
                  >
                    {label}
                  </button>
                )
              })}
            </div>
          </div>
          {code === 'other' && (
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Details</label>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={3}
                placeholder="Describe the reason (required)"
                className="mt-1 w-full rounded-md border border-white/15 bg-white/5 px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-red-500/60"
              />
            </div>
          )}
          {errorMessage && (
            <p className="text-xs text-red-400">{errorMessage}</p>
          )}
        </div>
        <div className="px-5 py-3 border-t border-[#2e2e2e] flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            className="px-3 py-1.5 rounded-md text-xs font-medium text-gray-300 hover:bg-white/5 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onConfirm(code!, code === 'other' ? text.trim() : undefined)}
            disabled={submitDisabled}
            className="px-3 py-1.5 rounded-md bg-red-500/20 border border-red-500/60 text-xs font-semibold text-red-100 hover:bg-red-500/30 transition-colors disabled:opacity-50"
          >
            {isSubmitting ? submittingLabel : primaryLabel}
          </button>
        </div>
      </div>
    </div>
  )
}
